import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import { formatDate, formatMoney, formatNumber } from '@awj/i18n';
import { Skeleton } from '@awj/ui';

import { DatePicker } from '~/components/DatePicker.tsx';
import { EmptyState } from '~/components/EmptyState.tsx';
import { ExportMenu } from '~/components/ExportMenu.tsx';
import { Icon } from '~/components/Icon.tsx';
import { PageTitle } from '~/components/PageTitle.tsx';
import { Pager } from '~/components/Pager.tsx';
import { Select } from '~/components/Select.tsx';
import { useI18n } from '~/i18n/index.tsx';
import { orderColumns } from '~/lib/export-columns.ts';
import { allOrders, useOrders } from '~/lib/order-queries.ts';
import { useRowLink } from '~/lib/row-link.ts';

const STATUSES = [
  'PendingPayment',
  'Paid',
  'Processing',
  'Shipped',
  'Delivered',
  'PaymentFailed',
  'Cancelled',
] as const;

const REFUND_DUE = 'RefundDue';

/**
 * Every order, narrowed.
 *
 * The default is newest first and unfiltered, because the commonest thing anyone does here is
 * look at what has just come in. Everything else — a status queue, a date range, a customer's
 * name — is a filter that lives in the URL, so "all the unpaid orders from last week" is a link
 * someone can send to a colleague.
 */
export function OrdersPage() {
  const { t, locale } = useI18n();
  const [params, setParams] = useSearchParams();

  const query = {
    search: params.get('q') ?? '',
    status: params.get('status') ?? '',
    from: params.get('from') ?? '',
    to: params.get('to') ?? '',
    page: Number(params.get('page')) || 1,
  };

  // "Refund due" sits in the status list because that is where staff look for a queue, but it is
  // a separate filter on the server: those orders are all Cancelled.
  const refundDue = query.status === REFUND_DUE;
  const orderFilters = { ...query, status: refundDue ? '' : query.status, refundDue };
  const orders = useOrders(orderFilters);
  const rowLink = useRowLink();

  const [search, setSearch] = useState(query.search);
  useEffect(() => setSearch(query.search), [query.search]);

  useEffect(() => {
    if (search === query.search) {
      return;
    }

    const timer = setTimeout(() => update({ q: search }), 350);

    return () => clearTimeout(timer);
  }, [search]); // eslint-disable-line react-hooks/exhaustive-deps

  function update(changes: Record<string, string>) {
    const next = new URLSearchParams(params);

    for (const [key, value] of Object.entries(changes)) {
      if (value) {
        next.set(key, value);
      } else {
        next.delete(key);
      }
    }

    if (!('page' in changes)) {
      next.delete('page');
    }

    setParams(next, { replace: true });
  }

  return (
    <>
      <PageTitle title={t('nav.orders')} />

      <div className="filter-bar">
        <div className="filter-bar__row">
          <label className="filter-bar__search">
            <span className="sr-only">{t('orders.search')}</span>
            <span className="filter-bar__search-icon">
              <Icon name="search" size={16} />
            </span>
            <input
              className="filter-bar__search-input"
              type="search"
              value={search}
              placeholder={t('orders.search')}
              onChange={(event) => setSearch(event.target.value)}
            />
          </label>

          <div className="filter-bar__controls">
            <div className="filter-bar__field">
              <label htmlFor="filter-status">{t('orders.status')}</label>
              <Select
                id="filter-status"
                value={query.status}
                onChange={(status) => update({ status })}
                placeholder={t('orders.allStatuses')}
                options={[
                  ...STATUSES.map((status) => ({
                    value: status,
                    label: t(`orderStatus.${status}` as 'orderStatus.Paid'),
                  })),
                  { value: REFUND_DUE, label: t('orders.refundDue') },
                ]}
              />
            </div>

          {/*
            Dates rather than a "last 30 days" preset: the question staff are usually answering is
            about a specific week — a courier's collection, a customer's complaint — and a preset
            cannot express that.
          */}
            <div className="filter-bar__field">
              <label htmlFor="filter-from">{t('orders.from')}</label>
              <DatePicker
                id="filter-from"
                value={query.from}
                max={query.to || undefined}
                clearable
                onChange={(from) => update({ from })}
              />
            </div>

            <div className="filter-bar__field">
              <label htmlFor="filter-to">{t('orders.to')}</label>
              <DatePicker
                id="filter-to"
                value={query.to}
                min={query.from || undefined}
                clearable
                onChange={(to) => update({ to })}
              />
            </div>

          {query.search || query.status || query.from || query.to ? (
            <button
              type="button"
              className="awj-button awj-button--ghost awj-button--sm"
              onClick={() => setParams(new URLSearchParams(), { replace: true })}
            >
              {t('common.clearFilters')}
            </button>
          ) : null}
          </div>

          <div className="filter-bar__actions">
            <ExportMenu
              file="orders"
              sheet={t('nav.orders')}
              columns={orderColumns(t, locale)}
              rows={() => allOrders(orderFilters)}
              hint={t('export.allMatching')}
              disabled={!orders.data?.total}
            />
          </div>
        </div>
      </div>

      {orders.isPending ? (
        <Skeleton height="320px" />
      ) : orders.isError ? (
        <EmptyState
          icon="alert"
          title={t('common.somethingWentWrong')}
          hint={t('common.somethingWentWrongHint')}
          action={
            <button type="button" className="awj-button awj-button--secondary" onClick={() => orders.refetch()}>
              {t('common.retry')}
            </button>
          }
        />
      ) : orders.data.items.length === 0 ? (
        <EmptyState icon="cart" title={t('orders.none')} hint={t('orders.noneHint')} tall />
      ) : (
        <div className="data-table">
            <div className="data-table__scroll" tabIndex={0} role="region" aria-label={t('nav.orders')}>
              <table className="data-table__table">
                <thead>
                  <tr>
                    <th className="data-table__th" scope="col">{t('orders.number')}</th>
                    <th className="data-table__th" scope="col">{t('orders.customer')}</th>
                    <th className="data-table__th" scope="col">{t('orders.placed')}</th>
                    <th className="data-table__th" scope="col">{t('orders.status')}</th>
                    <th scope="col" className="data-table__th data-table__cell--end">
                      {t('orders.items')}
                    </th>
                    <th scope="col" className="data-table__th data-table__cell--end">
                      {t('orders.total')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {orders.data.items.map((order) => (
                    <tr key={order.number} {...rowLink(`/orders/${order.number}`)}>
                      <td className="data-table__td">
                        <Link className="data-table__link mono" to={`/orders/${order.number}`}>
                          {order.number}
                        </Link>
                      </td>
                      <td className="data-table__td">
                        <bdi>{order.customerName}</bdi>
                        <span className="data-table__sub">
                          <bdi>{order.email}</bdi>
                        </span>
                      </td>
                      <td className="data-table__td">
                        <bdi>{formatDate(locale, order.placedAt)}</bdi>
                      </td>
                      <td className="data-table__td">
                        <span className={`status status--${order.status.toLowerCase()}`}>
                          {t(`orderStatus.${order.status}` as 'orderStatus.Paid')}
                        </span>
                      </td>
                      <td className="data-table__td data-table__cell--end">
                        <bdi>{formatNumber(locale, order.itemCount)}</bdi>
                      </td>
                      <td className="data-table__td data-table__cell--end">
                        <bdi>{formatMoney(locale, order.grandTotal)}</bdi>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <Pager
              page={orders.data.page}
              totalPages={orders.data.totalPages ?? 1}
              total={orders.data.total}
              onPage={(page) => update({ page: String(page) })}
            />
        </div>
      )}
    </>
  );
}
